import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { FaCrown, FaCoins, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import NotEnoughCoinsDialog from "./NotEnoughCoinsDialog";
import { useUser } from "../authMethods/UserContext";
import ChessButton from "./ChessButton";
import Grid2 from "@mui/material/Grid2";

const AccountPage = ({ purchasedPackages = [] }) => {
  const { userData } = useUser();
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [shouldScrollToCoins, setShouldScrollToCoins] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const formatPurchaseDate = (isoDate) => {
    if (!isoDate) return null;
    const date = new Date(isoDate);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleOpenDialog = (scrollAfter = false) => {
    setIsDialogOpen(true);
    setShouldScrollToCoins(scrollAfter);
  };

  const handleCloseDialog = () => setIsDialogOpen(false);

  const isValidPackage = (pkg) => {
    if (!pkg || !pkg.planId) return false;
    const cleanId = pkg.planId.trim().toLowerCase();
    return cleanId !== "basic" && cleanId !== "test" && cleanId !== "";
  };

  const resolveFeatureAvailability = (pkg, key) => {
    switch (key) {
      case "feature.viewerAccess": return pkg.viewerAccess;
      case "feature.analyticsDashboard": return pkg.analyticsDashboard;
      case "feature.adFree": return pkg.adFree;
      case "feature.priorityMatchmaking": return pkg.priorityMatchmaking;
      case "feature.earlyAccess": return pkg.earlyAccess;
      case "feature.teamCollaboration": return pkg.teamCollaboration;
      case "feature.unlimitedSavedGames": return pkg.unlimitedSavedGames;
      case "feature.unlimitedTeams": return pkg.unlimitedTeams;
      case "feature.coinsPerDay": return pkg.coinsPerDay > 0;
      case "feature.coinsPerMonth": return pkg.coinsPerMonth > 0;
      case "feature.maxGames": return pkg.maxGames > 0 || pkg.maxGames === -1;
      case "feature.maxTeams": return pkg.maxTeams > 0 || pkg.maxTeams === -1;
      case "feature.hintsPerGame": return pkg.hintsPerGame > 0;
      default: return false;
    }
  };

  const renderPackageCard = (pkg) => {
    if (!isValidPackage(pkg)) return null;

    return (
      <Card
        key={pkg.planId}
        sx={{
          border: "2px solid #4a707d",
          borderRadius: 3,
          boxShadow: 4,
          m: 2,
          width: 320,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" fontWeight="bold">{pkg.name}</Typography>
            <Chip
              icon={pkg.packageType === "subscription" ? <FaCrown /> : <FaCoins />}
              label={t(pkg.packageType === "subscription" ? "Subscription" : "Coin Package")}
              color={pkg.packageType === "subscription" ? "primary" : "secondary"}
            />
          </Box>

          {pkg.planId && pkg.planId.trim().toLowerCase() !== "basic" && (
            <Typography variant="body1" gutterBottom>{pkg.planId}</Typography>
          )}

          <Typography variant="body2" color="textSecondary">
            {t("Price")}: ${pkg.price} {pkg.currency}
          </Typography>

          {pkg.packageType === "subscription" && pkg.frequency && (
            <Typography variant="body2" color="textSecondary">
              {t("Billed every")}: {t(pkg.frequency)}
            </Typography>
          )}

          {pkg.coins > 0 && (
            <Typography variant="body2" color="textSecondary">
              {t("Coins included")}: {pkg.coins}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {pkg.featureKeys?.map((key, index) => {
            const isAvailable = resolveFeatureAvailability(pkg, key);
            const icon = isAvailable ? <FaCheckCircle color="#2e7d32" size="1.2em" /> : <FaTimesCircle color="#d32f2f" size="1.2em" />;
            return (
              <Box key={index} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                <Box>{icon}</Box>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {t(key, {
                    coinsPerDay: pkg.coinsPerDay,
                    coinsPerMonth: pkg.coinsPerMonth,
                    maxGames: pkg.maxGames,
                    maxTeams: pkg.maxTeams,
                    hintsPerGame: pkg.hintsPerGame,
                  })}
                </Typography>
              </Box>
            );
          })}

          {pkg.purchaseDate && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, display: "block", textAlign: "right", fontStyle: "italic" }}
            >
              {t("Purchased on")}: {formatPurchaseDate(pkg.purchaseDate)}
            </Typography>
          )}
        </CardContent>

        {pkg.level < 300 && (
          <Box sx={{ p: 2, pt: 0 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => handleOpenDialog(pkg.packageType !== "subscription")}
              color={pkg.packageType === "subscription" ? "warning" : "primary"}
            >
              {pkg.packageType === "subscription"
                ? t("Upgrade Plan")
                : t("Buy Again")}
            </Button>

            {pkg.packageType === "subscription" && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block", textAlign: "center", fontStyle: "italic" }}
              >
                {t("Switching plans will cancel your current one and activate the new subscription immediately")}
              </Typography>
            )}
          </Box>
        )}
      </Card>
    );
  };

  const subscriptions = userData?.coinsPackages
    .filter((pkg) => pkg.packageType === "subscription")
    .filter(isValidPackage);

  const coinPackages = userData?.coinsPackages
    .filter((pkg) => pkg.packageType === "package")
    .filter(isValidPackage);

  return (
    <Box sx={{ padding: isMobile ? 2 : 4 }}>
      <NotEnoughCoinsDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        buy={true}
        userData={userData}
        scrollToCoins={shouldScrollToCoins}
      />

      <Grid2
        container
        spacing={isMobile ? 4 : 10}
        justifyContent="center"
        sx={{ mt: isMobile ? 4 : 8 }}
      >
        <Grid2 item xs={12} md={6}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              color: "white",
              fontSize: isMobile ? "1.5rem" : "1.9rem",
              textAlign: "center",
            }}
          >
            {t("Your Subscriptions")}
          </Typography>
          <Box display="flex" flexWrap="wrap" justifyContent="center">
            {subscriptions?.length > 0 ? (
              subscriptions.map((pkg) => renderPackageCard(pkg))
            ) : (
              <Box textAlign="center" width="100%">
                <Typography variant="body2" sx={{ color: "white" }}>
                  {t("You don't have any active subscriptions")}
                </Typography>
                <Box mt={2}>
                  <ChessButton text={t("Purchase Subscription")} onPress={() => handleOpenDialog(false)} />
                </Box>
              </Box>
            )}
          </Box>
        </Grid2>

        <Grid2 item xs={12} md={6}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              color: "white",
              fontSize: isMobile ? "1.5rem" : "1.9rem",
              textAlign: "center",
            }}
          >
            {t("Your Coin Packages")}
          </Typography>
          <Box display="flex" flexWrap="wrap" justifyContent="center">
            {coinPackages?.length > 0 ? (
              coinPackages.map((pkg) => renderPackageCard(pkg))
            ) : (
              <Box textAlign="center" width="100%">
                <Typography variant="body2" sx={{ color: "white" }}>
                  {t("You haven't purchased any coin packages yet")}
                </Typography>
                <Box mt={2}>
                  <ChessButton text={t("Purchase Coins")} onPress={() => handleOpenDialog(true)} />
                </Box>
              </Box>
            )}
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default AccountPage;
